import { useState, useCallback } from "react"
import { useGet } from "../utils/apiComponent/useApi"
import { api } from "../utils/apiComponent/apiClient"
import { endpoints } from "../utils/endpoints"

/**
 * Hook pour récupérer la progression de tous les cours de l'utilisateur
 * GET /course-progress/courses
 */
export function useCourseProgress() {
  const {
    data,
    loading,
    error,
    execute
  } = useGet(endpoints.courseProgress)

  // L'API retourne { success: true, data: [...] } ou directement un array
  // Extraire les données de la réponse
  const progress = data?.data || data || []

  return {
    progress,
    loading,
    error,
    refetch: execute
  }
}

/**
 * Hook pour démarrer un cours (créer la progression)
 * POST /course-progress/courses/:courseId/start
 */
export function useStartCourse() {
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const start = useCallback(async (courseId) => {
    setLoading(true)
    setError(null)
    try {
      const response = await api.post(endpoints.startCourse(courseId), null)
      setResult(response)
      return response
    } catch (err) {
      // Si l'erreur est 400 (cours déjà démarré) ou 422 (progression déjà existante), on l'ignore silencieusement
      if (err?.status === 400 || err?.status === 422 || err?.statusCode === 400 || err?.statusCode === 422) {
        // Cours déjà démarré ou progression déjà existante, c'est normal, on retourne null sans erreur
        setLoading(false)
        return null
      }
      // Pour les autres erreurs, on log et on throw
      console.error('Error starting course:', err)
      setError(err)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    start,
    loading,
    error,
    result
  }
}

/**
 * Hook pour démarrer un bloc (créer la progression du bloc)
 * POST /course-progress/blocks/:blockId/start
 */
export function useStartBlock() {
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const start = useCallback(async (blockId) => {
    setLoading(true)
    setError(null)
    try {
      const response = await api.post(endpoints.startBlock(blockId), null)
      setResult(response)
      return response
    } catch (err) {
      // Si l'erreur est 422 (progression déjà existante), on l'ignore silencieusement
      if (err?.status === 422 || err?.statusCode === 422) {
        // Progression déjà existante, c'est normal, on retourne null sans erreur
        setLoading(false)
        return null
      }
      // Pour les autres erreurs, on log et on throw
      console.error('Error starting block:', err)
      setError(err)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    start,
    loading,
    error,
    result
  }
}

/**
 * Hook pour marquer un bloc comme terminé
 * POST /course-progress/blocks/:blockId/complete
 */
export function useCompleteBlock() {
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const complete = useCallback(async (blockId, data = {}) => {
    setLoading(true)
    setError(null)
    try {
      const response = await api.post(endpoints.completeBlock(blockId), data)
      setResult(response)
      return response
    } catch (err) {
      console.error('Error completing block:', err)
      setError(err)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    complete,
    loading,
    error,
    result
  }
}

/**
 * Hook pour valider une leçon (marque tous ses blocs comme complétés)
 * POST /course-progress/lessons/:lessonId/complete
 */
export function useCompleteLesson() {
  const [result, setResult] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const complete = useCallback(async (lessonId) => {
    setLoading(true)
    setError(null)
    try {
      const response = await api.post(endpoints.completeLesson(lessonId), null)
      setResult(response)
      return response
    } catch (err) {
      console.error('Error completing lesson:', err)
      setError(err)
      throw err
    } finally {
      setLoading(false)
    }
  }, [])

  return {
    complete,
    loading,
    error,
    result
  }
}

